import { useState, useRef, useEffect } from 'react';
import { DropDownInput2 } from '../_input/_dropdown2';
import { Price } from '../__simple/_price';

function AccordionPanel({ title, defaultOpen = false, children }) {
    const [open, setOpen] = useState(defaultOpen);
    const bodyRef = useRef(null);
    const [height, setHeight] = useState(defaultOpen ? 'auto' : '0px');

    useEffect(() => {
        if (!bodyRef.current) return;
        if (open) {
            const h = bodyRef.current.scrollHeight;
            setHeight(`${h}px`);
            const t = setTimeout(() => setHeight('auto'), 380);
            return () => clearTimeout(t);
        } else {
            const h = bodyRef.current.scrollHeight;
            setHeight(`${h}px`);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => setHeight('0px'));
            });
        }
    }, [open]);

    return (
        <div
            className="hj-premium-card"
            style={{
                marginBottom: '2rem',
                overflow: 'hidden',
                padding: 0,
                border: '1px solid rgba(0,0,0,0.06)'
            }}
        >
            <div
                style={{
                    padding: '20px 24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    background: open ? 'var(--co-green-pale)' : 'var(--co-surface)',
                    transition: 'background .3s ease'
                }}
                onClick={() => setOpen(v => !v)}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: 'var(--co-green)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <rect x="2" y="4" width="20" height="16" rx="2" />
                            <line x1="2" y1="10" x2="22" y2="10" />
                        </svg>
                    </div>
                    <h3 style={{ fontSize: '18px', fontWeight: 800, color: 'var(--co-t1)', margin: 0 }}>{title}</h3>
                </div>
                <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: 'rgba(0,0,0,0.04)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'transform .4s var(--co-ease)',
                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
                }}>
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </div>
            </div>

            <div
                ref={bodyRef}
                style={{
                    overflow: 'hidden',
                    height: height,
                    transition: height === 'auto'
                        ? 'none'
                        : 'height .4s var(--co-ease), opacity .3s ease',
                    opacity: open ? 1 : 0,
                    background: 'var(--co-surface)'
                }}
            >
                <div style={{ padding: '0 24px 24px' }}>
                    {children}
                </div>
            </div>
        </div>
    );
}

export default function FinanceCalculator({
    trans,
    activeFilterMenu,
    handleOnActive,
    vehicle,
    finance,
    selectedFinancePeriod,
    setSelectedFinancePeriod,
}) {
    return (
        <AccordionPanel title={trans('new:how_buy_online')} defaultOpen={true}>
            {/* Pill tabs switcher */}
            <div
                style={{
                    display: 'flex',
                    background: 'var(--co-bg2)',
                    borderRadius: '12px',
                    padding: '5px',
                    gap: '5px',
                    marginTop: '1rem',
                    marginBottom: '2rem'
                }}
            >
                {[
                    { label: trans('new:cash'), id: 0 },
                    { label: trans('finance:periods'), id: 1 }
                ].map(tab => (
                    <div
                        key={tab.id}
                        className="nice-pointer"
                        onClick={() => handleOnActive(tab.id)}
                        style={{
                            flex: 1,
                            textAlign: 'center',
                            padding: '10px',
                            borderRadius: '10px',
                            fontWeight: 700,
                            fontSize: '14px',
                            transition: 'all .3s var(--co-ease)',
                            background: activeFilterMenu === tab.id ? 'var(--co-surface)' : 'transparent',
                            color: activeFilterMenu === tab.id ? 'var(--co-green)' : 'var(--co-t3)',
                            boxShadow: activeFilterMenu === tab.id ? 'var(--co-shadow-sm)' : 'none'
                        }}
                    >
                        {tab.label}
                    </div>
                ))}
            </div>

            <div className="finance-results">
                {activeFilterMenu === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px', borderRadius: '20px', background: 'var(--co-green-pale)' }}>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--co-t3)', marginBottom: '8px' }}>{trans('new:Incloding_VAT')}</div>
                        <div style={{ fontSize: '32px', fontWeight: 900, color: 'var(--co-green)' }}>
                            {vehicle?.minPrice && <Price price={parseFloat(vehicle?.minPrice)} />}
                        </div>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {/* Main Installment Display */}
                        <div style={{ textAlign: 'center', padding: '24px', borderRadius: '20px', background: 'var(--co-green-pale)', border: '1px solid rgba(24,169,78,.1)' }}>
                            <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--co-t3)', marginBottom: '8px' }}>{trans('new:price_is_estimated')}</div>
                            <div style={{ fontSize: '36px', fontWeight: 900, color: 'var(--co-green)' }}>
                                {finance?.price && <Price price={parseFloat(finance?.price.toFixed(2))} />}
                            </div>
                        </div>

                        {/* Details Grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                            <div className="hj-premium-card" style={{ padding: '16px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: 'none', border: '1px solid rgba(0,0,0,.06)' }}>
                                <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--co-t3)', marginBottom: '8px', textAlign: 'center' }}>{trans('finance:installment_duration')}</div>
                                <div style={{ fontWeight: 800, fontSize: '14px', color: 'var(--co-t1)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <DropDownInput2
                                        bgNone={true}
                                        fieldTextClass="text-16 text-bold text-green"
                                        classDropdownField="nice-shadow-0 p-0 m-0"
                                        defaultOption={{ text: 24, value: 24 }}
                                        options={[
                                            { text: 24, value: 24 },
                                            { text: 36, value: 36 },
                                            { text: 48, value: 48 },
                                            { text: 60, value: 60 },
                                        ]}
                                        onSelect={(v) => setSelectedFinancePeriod(v)}
                                        selectedValue={selectedFinancePeriod}
                                    />
                                    <span>{trans('info:month')}</span>
                                </div>
                            </div>

                            <div className="hj-premium-card" style={{ padding: '16px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: 'none', border: '1px solid rgba(0,0,0,.06)' }}>
                                <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--co-t3)', marginBottom: '8px', textAlign: 'center' }}>{trans('new:first_batch')}</div>
                                <div style={{ fontWeight: 800, fontSize: '14px', color: 'var(--co-t1)' }}>
                                    {finance?.first && <Price price={parseFloat(finance?.first.toFixed(2))} hideCurrency={true} />}
                                </div>
                            </div>

                            <div className="hj-premium-card" style={{ padding: '16px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: 'none', border: '1px solid rgba(0,0,0,.06)' }}>
                                <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--co-t3)', marginBottom: '8px', textAlign: 'center' }}>{trans('new:last_batch')}</div>
                                <div style={{ fontWeight: 800, fontSize: '14px', color: 'var(--co-t1)' }}>
                                    {finance?.last && <Price price={parseFloat(finance?.last.toFixed(2))} hideCurrency={true} />}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AccordionPanel>
    );
}
